import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminAPI } from '../../services/adminApi';
import { formatMoney } from '../../utils/formatMoney';
import { LoadFailed } from '../settings/instantSettings';
import { Column, DataTable, EmptyState, ListPage, Stat, Stats } from '../shared/listPage';

/**
 * Super admin → «Dashboard» στη διάταξη «Πίνακας» (14/10/2026): οι αριθμοί σε δύο γραμμές κειμένου
 * (Stats — ως τότε τέσσερις χρωματιστές κάρτες με εικονίδιο και μία κάρτα «Κατάσταση παραγγελιών») και
 * τα έσοδα ανά κατάστημα σε πίνακα. Οι «Top 4» κάρτες έγιναν η στήλη «Θέση»: ο server στέλνει ήδη τα
 * καταστήματα από τα μεγαλύτερα έσοδα στα μικρότερα (GET /api/admin/dashboard, ίδιο αίτημα με πριν).
 */

interface DashboardStats {
  stores: number;
  employees: number;
  orders: {
    total: number;
    pending: number;
    accepted: number;
    completed: number;
    today: number;
  };
}

interface StoreRevenue {
  id: number;
  name: string;
  order_count: number;
  total_revenue: number;
}

// Η PG στέλνει τα COUNT/SUM ως κείμενο.
const num = (v: unknown): number => {
  const n = parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
};

const NUMBER = new Intl.NumberFormat('el-GR');
const count = (v: unknown): string => NUMBER.format(num(v));

interface RevenueRow {
  id: number;
  rank: number;
  name: string;
  orders: number;
  revenue: number;
  avg: number;
}

const SuperAdminDashboardTab: React.FC = () => {
  const { t } = useTranslation(['superAdminPanel', 'admin', 'common']);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [revenueByStore, setRevenueByStore] = useState<StoreRevenue[]>([]);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const response: any = await adminAPI.getDashboard();
      setStats(response.statistics);
      setRevenueByStore(response.revenueByStore || []);
      setError(null);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      setError(t('admin:failedToLoadData'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchDashboard();
    // Μία φορά στο άνοιγμα της σελίδας, όπως πριν.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo<RevenueRow[]>(() => revenueByStore.map((s, i) => {
    const revenue = num(s.total_revenue);
    const orders = num(s.order_count);
    return { id: s.id, rank: i + 1, name: s.name, orders, revenue, avg: orders > 0 ? revenue / orders : 0 };
  }), [revenueByStore]);

  const totalRevenue = rows.reduce((sum, r) => sum + r.revenue, 0);
  const totalOrders = rows.reduce((sum, r) => sum + r.orders, 0);
  const pending = num(stats?.orders.pending);

  const columns = useMemo<Column<RevenueRow>[]>(() => [
    {
      key: 'rank',
      header: t('superAdminPanel:dashboard.colRank'),
      width: '96px',
      hideBelow: 'narrow',
      sortable: true,
      render: r => <span className="tabular-nums text-t3">{r.rank}</span>,
    },
    {
      key: 'name',
      header: t('superAdminPanel:dashboard.colStore'),
      width: 'minmax(200px, 2fr)',
      sortable: true,
      render: r => <span className="font-medium">{r.name}</span>,
      stackRender: r => `${r.rank}. ${r.name}`,
    },
    {
      key: 'orders',
      header: t('superAdminPanel:dashboard.colOrders'),
      width: '140px',
      align: 'right',
      sortable: true,
      render: r => <span className="text-t2">{count(r.orders)}</span>,
      stackRender: r => t('superAdminPanel:dashboard.orders', { count: r.orders, n: count(r.orders) }),
    },
    {
      key: 'revenue',
      header: t('superAdminPanel:dashboard.colRevenue'),
      width: '150px',
      align: 'right',
      sortable: true,
      stack: 'end',
      render: r => <span className="font-medium">{formatMoney(r.revenue)}</span>,
    },
    {
      key: 'avg',
      header: t('superAdminPanel:dashboard.colAvg'),
      width: '140px',
      align: 'right',
      sortable: true,
      render: r => <span className="text-t3">{formatMoney(r.avg)}</span>,
      stackRender: r => t('superAdminPanel:dashboard.avgShort', { amount: formatMoney(r.avg) }),
    },
  ], [t]);

  const firstLoad = loading && stats === null;
  // Όσο φορτώνει για πρώτη φορά οι αριθμοί δεν είναι «0»: μια παύλα στη θέση τους.
  const value = (v: unknown) => (firstLoad ? '—' : count(v));

  return (
    <ListPage
      eyebrow={t('superAdminPanel:nav.groups.overview')}
      title={t('superAdminPanel:nav.dashboard')}
      subtitle={t('superAdminPanel:dashboard.subtitle')}
      testId="sa-dashboard"
    >
      {error ? (
        <div className="border-t border-line pt-5">
          <LoadFailed message={error} onRetry={fetchDashboard} />
        </div>
      ) : (
        <>
          {/* Οι αριθμοί σε γραμμές κειμένου — όχι κουτιά */}
          <div className="border-t border-line py-4 space-y-2.5" aria-busy={firstLoad || undefined}>
            <Stats testId="sa-dashboard-stats">
              <Stat label={t('superAdminPanel:dashboard.activeStores')} value={value(stats?.stores)} />
              <Stat label={t('superAdminPanel:dashboard.activeEmployees')} value={value(stats?.employees)} />
              <Stat
                label={t('superAdminPanel:dashboard.ordersToday')}
                value={value(stats?.orders.today)}
                hint={!firstLoad && pending > 0 ? t('superAdminPanel:dashboard.pending', { count: pending, n: count(pending) }) : undefined}
              />
              <Stat
                label={t('superAdminPanel:dashboard.revenue30')}
                value={firstLoad ? '—' : formatMoney(totalRevenue)}
                hint={firstLoad ? undefined : t('superAdminPanel:dashboard.orders', { count: totalOrders, n: count(totalOrders) })}
              />
            </Stats>
            <Stats testId="sa-dashboard-status">
              <span className="text-t3 font-medium">{t('superAdminPanel:dashboard.statusTitle')}</span>
              <Stat label={t('superAdminPanel:dashboard.statusPending')} value={value(stats?.orders.pending)} />
              <Stat label={t('superAdminPanel:dashboard.statusAccepted')} value={value(stats?.orders.accepted)} />
              <Stat label={t('superAdminPanel:dashboard.statusCompleted')} value={value(stats?.orders.completed)} />
              <Stat label={t('superAdminPanel:dashboard.statusTotal')} value={value(stats?.orders.total)} />
            </Stats>
          </div>

          {/* Τίτλος ενότητας όπως στο «Φύλλο» (xs-sec-title: μικρή μπάρα στο χρώμα της εφαρμογής) */}
          <div className="border-t border-line pt-5 pb-3">
            <h2 className="xs-sec-title"><span>{t('superAdminPanel:dashboard.revenueTitle')}</span></h2>
            <p className="xs-sec-hint">{t('superAdminPanel:dashboard.revenueHint')}</p>
          </div>
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={r => r.id}
            loading={loading}
            stickyHeader
            ariaLabel={t('superAdminPanel:dashboard.revenueTitle')}
            rowLabel={r => t('superAdminPanel:dashboard.rowLabel', { rank: r.rank, name: r.name })}
            empty={<EmptyState title={t('superAdminPanel:dashboard.empty')} />}
            testId="sa-revenue-table"
          />
        </>
      )}
    </ListPage>
  );
};

export default SuperAdminDashboardTab;
